import * as sensorDetailsActions from '@Actions/sensorDetails';
import { AsyncPollSensorTbltActionPayload } from '@Actions/sensorDetails';
import { getStatus, peekSensorService } from '@Services/device';
import {
	SensorPeekAsyncResponse,
	SensorPeekRequests,
	SensorPeekShadow,
} from '@Types/peek';
import { PeekDataParser } from '@Utils/peekDataParser';
import {
	error as snackbarError,
	warning as snackbarWarn,
} from '@innovyze/stylovyze';
import { PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { Task } from 'redux-saga';
import {
	call,
	cancel,
	cancelled,
	delay,
	fork,
	put,
	take,
	takeLatest,
} from 'redux-saga/effects';

const SHADOW_NAME = 'SensorPeek';
const INITIAL_DELAY_SEC = 3; // ? possible lambda's cold start time
const BETWEEN_POLLING_TRIES_DELAY_SEC = 5; // ? delay between polling tries

function* peekSensorSaga(
	action: PayloadAction<sensorDetailsActions.PeekSensorActionParams>,
) {
	const {
		troubleshootSensor,
		peekSensorAsyncResolved,
		pollPeekAsyncResults,
		peekSensorAsyncRejected,
	} = sensorDetailsActions;
	const { peekForm, timeoutMs } = action.payload;
	try {
		const peekResponse: SensorPeekAsyncResponse | 'timeout' = yield call(
			peekSensorService,
			peekForm,
		);
		if (peekResponse === 'timeout') {
			yield put(troubleshootSensor({ peekForm, timeoutMs }));
		} else {
			yield put(peekSensorAsyncResolved(peekResponse));
			if (peekResponse.operationResult === 'success') {
				yield put(
					pollPeekAsyncResults({ peekForm, peekResponse, timeoutMs }),
				);
			}
		}
	} catch (e) {
		console.error(e);
		yield put(peekSensorAsyncRejected());
		console.error('Peek Async Failed');
		yield put(troubleshootSensor({ peekForm, timeoutMs }));
	}
}

type PollResponse = AxiosResponse<SensorPeekShadow>;
function* pollSensorPeekStateSaga(
	action: PayloadAction<sensorDetailsActions.PollPeekSensorActionParams>,
) {
	const {
		troubleshootSensor,
		pollPeekAsyncResultsStop,
		pollPeekAsyncResultsResolved,
		pollPeekAsyncResultsRejected,
	} = sensorDetailsActions;
	const {
		peekForm,
		peekResponse: { uid },
		timeoutMs,
	} = action.payload;

	function getReported(sh: SensorPeekShadow): SensorPeekRequests {
		return sh?.status?.payload?.state?.reported;
	}

	try {
		if (!uid) throw new Error('Invalid uid id');
		const shadowName = `${SHADOW_NAME}-${uid}`;
		while (true) {
			const res: PollResponse = yield call(getStatus, shadowName);
			const result = getReported(res.data);
			if (result?.payload?.status === 'FINISHED') {
				// ? first element is the headers
				if (result?.payload?.results?.length >= 2) {
					// * Stop success
					const res = PeekDataParser.parseDataToGraphRecord(
						result?.payload?.results,
					);
					yield put(pollPeekAsyncResultsResolved(res));
					yield put(pollPeekAsyncResultsStop()); // * Stop polling
				} else {
					yield put(troubleshootSensor({ peekForm, timeoutMs })); // * start troubleshooting
					yield put(pollPeekAsyncResultsRejected()); // * Stop polling (error) also cancels the saga (need to be at the end)
				}
			} else if (result?.payload?.status === 'ERROR') {
				yield put(troubleshootSensor({ peekForm, timeoutMs })); // * start troubleshooting
				yield put(pollPeekAsyncResultsRejected()); // * Stop polling (error)
			} else {
				// * Poll continues...
				yield delay(1000 * BETWEEN_POLLING_TRIES_DELAY_SEC);
			}
		}
	} catch (e) {
		console.error(e);
		yield put(snackbarError('Poll sensor state failed'));
		yield put(pollPeekAsyncResultsRejected());
	} finally {
		// * This block gets called if the task (saga) gets cancelled
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		if (yield cancelled()) console.log('poll was cancelled');
	}
}

function* stopPollSensorPeekStateSaga(
	action: PayloadAction<AsyncPollSensorTbltActionPayload>,
) {
	const { pollPeekAsyncResultsStop } = sensorDetailsActions;
	const tOut = action.payload.timeoutMs / 1000;
	console.log(`pollPeekAsyncResults will stop after ${tOut} seconds!`);
	try {
		yield delay(tOut * 1000);
		yield put(snackbarWarn(`Sensor test timed out after ${tOut} seconds`));
		yield put(pollPeekAsyncResultsStop()); // * Stop polling
	} catch (error) {
		console.log(error);
	} finally {
		// * This block gets called if the task (saga) gets cancelled
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		if (yield cancelled()) console.log('stop condition was cancelled');
	}
}

function* watchPeekSensor() {
	yield takeLatest(
		sensorDetailsActions.peekSensorAsync, //
		peekSensorSaga,
	);
}

function* watchPollPeekResultsState() {
	const {
		pollPeekAsyncResults,
		pollPeekAsyncResultsStop,
		pollPeekAsyncResultsResolved,
		pollPeekAsyncResultsRejected,
	} = sensorDetailsActions;
	// * Custom implementation, similar to takeLatest but it's cancellable. Check pollSensorPeekStateSaga
	while (true) {
		const action: PayloadAction<AsyncPollSensorTbltActionPayload> =
			yield take([pollPeekAsyncResults]);
		console.log('pollPeekAsyncResults action taken!');
		yield delay(1000 * INITIAL_DELAY_SEC);
		const task: Task = yield fork(pollSensorPeekStateSaga, action as never);
		const stopConditionTask: Task = yield fork(
			stopPollSensorPeekStateSaga,
			action,
		);
		yield take([
			pollPeekAsyncResultsStop,
			pollPeekAsyncResultsResolved,
			pollPeekAsyncResultsRejected,
		]);
		if (task.isRunning()) yield cancel(task);
		if (stopConditionTask.isRunning()) yield cancel(stopConditionTask);
	}
}

export default [
	watchPeekSensor(), //
	watchPollPeekResultsState(),
];
